/* import __COLOCATED_TEMPLATE__ from './saml-settings.hbs'; */
/* RESPONSIBLE TEAM: team-app-security */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { run } from '@ember/runloop';
import {
  FULL_ACCESS_ROLE_NAME,
  NO_ACCESS_ROLE_NAME,
  VIEW_ONLY_ROLE_NAME,
} from 'embercom/components/table-cells/teammate-list/standalone-access-level-selector';
import {
  getUnallowedPermissionsForSeats,
  getSeatDependentPermissions,
} from 'embercom/lib/seats-permissions-map';
import ModelWithPermissions from 'embercom/models/model-with-permissions';
import { UNLIMITED_USAGE } from 'embercom/lib/settings/copilot-access-editor/constants';

const SEAT_TYPE_FULL = 'core';
const SEAT_TYPE_LITE = 'light';

export default class SamlSettings extends Component {
  @service appService;
  @service notificationsService;
  @service store;
  @service intl;
  @service copilotDataService;
  @service customerService;
  @service permissionsHierarchyService;

  @tracked idpSsoTargetUrl = this.args.samlAccount.idpSsoTargetUrl?.replace('https://', '');
  @tracked shouldShowPermissionsModal = false;
  @tracked permissionsObject = this._getDefaultPermissionsObject();

  // Standalone-specific tracked properties
  @tracked selectedStandaloneRoleId = this._getInitialStandaloneRoleId();
  @tracked copilotUsage = this._getInitialCopilotUsage();
  @tracked isLoadingCopilotData = false;

  @tracked seatType = this._getInitialSeatType();
  // Always require explicit seat type selection before allowing unlimited copilot
  // Even if there's a saved seat_type, user must confirm their choice in this session
  @tracked hasUserSelectedSeatType = false;

  _getDefaultPermissionsObject() {
    return this._getPermissionsObject(this.args.samlAccount.jitSettings);
  }

  _getPermissionsObject(permissions) {
    this.store.peekAll('jit-settings-permissions').forEach((record) => record.unloadRecord());
    permissions = permissions || {};
    let permissionsObject = this.store.createRecord('jit-settings-permissions', permissions);
    return permissionsObject;
  }

  get canEnableJustInTimeProvisioning() {
    return !this.appService.app.is_test_app;
  }

  get isStandalone() {
    return this.appService.app.canUseStandalone;
  }

  get standaloneRoles() {
    return this.store.peekAll('role').toArray();
  }

  get sortedStandaloneRoles() {
    // Sort roles: Full Access first, then View Only, then No Access
    let roleOrder = {
      [FULL_ACCESS_ROLE_NAME]: 0,
      [VIEW_ONLY_ROLE_NAME]: 1,
      [NO_ACCESS_ROLE_NAME]: 2,
    };
    return this.standaloneRoles.sort((a, b) => {
      return (roleOrder[a.name] ?? 99) - (roleOrder[b.name] ?? 99);
    });
  }

  get standaloneWorkspaceRoles() {
    // Show No Access role only when copilot access is enabled (unlimited)
    // (No Access means they have copilot seat but no workspace access)
    if (this.copilotUsage === 'unlimited') {
      return this.sortedStandaloneRoles;
    }
    // Otherwise, filter out No Access role
    return this.sortedStandaloneRoles.filter((role) => role.name !== NO_ACCESS_ROLE_NAME);
  }

  get fullAccessRole() {
    return this.standaloneRoles.find((role) => role.name === FULL_ACCESS_ROLE_NAME);
  }

  get noAccessRole() {
    return this.standaloneRoles.find((role) => role.name === NO_ACCESS_ROLE_NAME);
  }

  get copilotSeatPrice() {
    let priceInCents = this.copilotDataService.copilotData?.price || 0;
    return this.intl.formatNumber(priceInCents / 100, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    });
  }

  _getInitialStandaloneRoleId() {
    let jitSettings = this.args.samlAccount.jitSettings;
    // Convert to string to match Ember Data role.id type
    if (jitSettings?.standalone_role_id != null) {
      return String(jitSettings.standalone_role_id);
    }
    return this.fullAccessRole?.id || null;
  }

  _getInitialCopilotUsage() {
    return this.args.samlAccount.jitSettings?.copilot_usage || 'off';
  }

  _getInitialSeatType() {
    return this.args.samlAccount.jitSettings?.seat_type || SEAT_TYPE_FULL;
  }

  get canSelectSeatType() {
    return (
      !this.isStandalone &&
      this.appService.app.hasMultipleSeatTypes &&
      this.appService.app.canUseFeature('jit-seat-type-selection')
    );
  }

  get availableSeatTypes() {
    let customerSeatTypes = this.customerService.customer?.availableSeatTypes || [];
    return customerSeatTypes.filter((type) => type === SEAT_TYPE_FULL || type === SEAT_TYPE_LITE);
  }

  get canSelectLiteSeat() {
    return this.availableSeatTypes.includes(SEAT_TYPE_LITE);
  }

  get isLiteSeatSelected() {
    return this.seatType === SEAT_TYPE_LITE;
  }

  get disabledCopilotOptions() {
    if (this.canSelectSeatType && this.hasUserSelectedSeatType && !this.isLiteSeatSelected) {
      return [];
    }
    return [UNLIMITED_USAGE];
  }

  get jitSeatTypes() {
    if (this.canSelectSeatType) {
      return [this.seatType];
    }
    return [SEAT_TYPE_FULL];
  }

  get unallowedPermissionList() {
    if (!this.canSelectSeatType) {
      return [];
    }
    return getUnallowedPermissionsForSeats(
      this.jitSeatTypes,
      this.permissionsHierarchyService.seatPermissionMap,
    );
  }

  get seatDependentPermissionList() {
    if (!this.canSelectSeatType) {
      return [];
    }
    return getSeatDependentPermissions(this.permissionsHierarchyService.seatPermissionMap, true);
  }

  toggleOffDisallowedPermissions() {
    if (!this.canSelectSeatType || !this.permissionsObject) {
      return;
    }
    this.unallowedPermissionList.forEach((permissionKey) => {
      if (ModelWithPermissions.editablePermissionKeys.includes(permissionKey)) {
        if (this.permissionsObject[permissionKey] !== false) {
          this.permissionsObject[permissionKey] = false;
        }
      }
    });
  }

  @action
  updateIdpSsoTargetUrl() {
    // We need to run this in a runloop because the the on modifier does not schedule the event listener onto the run loop
    // https://github.com/emberjs/ember.js/issues/19222#issuecomment-1049773929
    run(() => {
      let targetUrl = this.idpSsoTargetUrl.trim().replace('https://', '') || '';
      this.idpSsoTargetUrl = targetUrl;
      this.args.samlAccount.idpSsoTargetUrl = `https://${targetUrl}`;
    });
  }

  @action
  updateIdpSsoTargetUrlOnPaste(event) {
    // We need to run this in a runloop because the the on modifier does not schedule the event listener onto the run loop
    // https://github.com/emberjs/ember.js/issues/19222#issuecomment-1049773929
    run(() => {
      let targetUrl = (event.clipboardData || window.clipboardData).getData('text');
      targetUrl = targetUrl.trim().replace('https://', '') || '';
      this.idpSsoTargetUrl = targetUrl;
      this.args.samlAccount.idpSsoTargetUrl = `https://${targetUrl}`;
    });
  }

  @action
  openPermissionsModal() {
    if (this.isStandalone) {
      // Load copilot data lazily for standalone apps (don't block modal opening)
      this._loadCopilotDataAsync();
    }
    this.shouldShowPermissionsModal = true;
  }

  async _loadCopilotDataAsync() {
    this.isLoadingCopilotData = true;
    await this.copilotDataService.ensureSetup();
    this.isLoadingCopilotData = false;
  }

  @action
  onStandaloneRoleChange(roleId) {
    this.selectedStandaloneRoleId = roleId;
  }

  @action
  onCopilotUsageChange(value) {
    // If turning off copilot access and currently selected role is "No Access",
    // switch to Full Access (since No Access requires copilot)
    if (value === 'off' && this.selectedStandaloneRoleId === this.noAccessRole?.id) {
      this.selectedStandaloneRoleId = this.fullAccessRole?.id;
    }
    this.copilotUsage = value;
  }

  @action
  onSeatTypeChange(value) {
    this.seatType = value;
    this.hasUserSelectedSeatType = true;
    if (value === SEAT_TYPE_LITE && this.permissionsObject) {
      this.permissionsObject.copilotAccess = 'off';
    }
    this.toggleOffDisallowedPermissions();
  }

  @action
  updatePermissions() {
    try {
      if (this.isStandalone) {
        this.args.samlAccount.jitSettings = {
          standalone_role_id: this.selectedStandaloneRoleId,
          copilot_usage: this.copilotUsage,
        };
      } else {
        this.permissionsObject.validate();
        let jitSettings = {
          ...this.permissionsObject.permissions(),
          ...this.permissionsObject.conversationAccess,
        };
        if (this.canSelectSeatType) {
          jitSettings.seat_type = this.seatType;
        }
        this.args.samlAccount.jitSettings = jitSettings;
      }
      this.shouldShowPermissionsModal = false;
    } catch (error) {
      this.notificationsService.notifyError(error.message);
    }
  }

  @action
  closePermissionsModalAndRollback() {
    this.permissionsObject = this._getPermissionsObject(this.args.samlAccount.jitSettings);
    // Reset standalone state
    if (this.isStandalone) {
      this.selectedStandaloneRoleId = this._getInitialStandaloneRoleId();
      this.copilotUsage = this._getInitialCopilotUsage();
    }
    if (this.canSelectSeatType) {
      this.seatType = this._getInitialSeatType();
      this.hasUserSelectedSeatType = false;
    }
    this.shouldShowPermissionsModal = false;
  }
}
